import React, { useEffect, useState } from 'react';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { logError } from '@jwp/ott-common/src/logger';
import env from '@jwp/ott-common/src/env';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

import { useAriaAnnouncer } from '../../../AnnouncementProvider/AnnoucementProvider';
import TextField from '../../../../components/form-fields/TextField/TextField';
import PasswordField from '../../../../components/form-fields/PasswordField/PasswordField';
import { modalURLFromLocation } from '../../../../utils/location';
import LoadingOverlay from '../../../../components/LoadingOverlay/LoadingOverlay';
import FormLayout from '../../../../components/FormLayout/FormLayout';
import ResendCodeLink from '../../../../components/ResendCodeLink/ResendCodeLink';
import FormFeedback from '../../../../components/FormFeedback/FormFeedback';
import Button from '../../../../components/Button/Button';

type ResetPasswordFormData = {
  code: string;
  newPassword: string;
};

const ResetPasswordChallenge = ({ email }: { email: string }) => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const announce = useAriaAnnouncer();
  const siteName = useConfigStore((state) => state.config.siteName);
  const accountController = getModule(AccountController);
  const [step, setStep] = useState<'code' | 'password' | 'wrongCode'>('code');

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationKey: ['reset-password-challenge'],
    mutationFn: async () => accountController.resetPassword(email, `${env.APP_PUBLIC_URL}/?u=edit-password`),
  });

  useEffect(() => {
    mutate(); // send code to the user immediately
  }, [mutate]);

  const { values, errors, submitting, handleSubmit, handleChange } = useForm<ResetPasswordFormData>({
    initialValues: { code: '', newPassword: '' },
    validationSchema: object().shape({
      code: string().required(t('login.field_required', { field: t('login.code') })),
      newPassword: step === 'code' || step === 'wrongCode' ? string().default('') : string().required(t('login.field_required', { field: t('login.code') })),
    }),
    onSubmit: ({ code, newPassword }) => {
      if (step === 'code' || step === 'wrongCode') {
        return setStep('password');
      }

      return accountController.changePasswordWithToken(email, newPassword, code, newPassword);
    },
    onSubmitSuccess: () => {
      if (step === 'password') {
        announce(t('login.sign_in_success'), 'success');
        navigate(modalURLFromLocation(location, 'login', { message: t('reset.password_reset_success'), email, id: undefined }));
      }
    },
    onSubmitError: ({ error, resetValue }) => {
      if (error instanceof Error && error.message.includes('verification code')) {
        setStep('wrongCode');
        resetValue('newPassword');
      }

      logError('ResetPasswordChallenge', 'Failed to reset the password', { error });
    },
  });

  if (error) {
    return (
      <div>
        <FormFeedback variant="error">{t('challenge.reset_password.unknown_error')}</FormFeedback>
        <Button label={t('common:alert.close')} onClick={() => navigate({ search: '' })} fullWidth />
      </div>
    );
  }

  if (step === 'password') {
    return (
      <FormLayout
        title={t('reset.password_reset')}
        errors={errors}
        submitLabel={t('challenge.reset_password.confirm')}
        submitting={submitting}
        onSubmit={handleSubmit}
      >
        <PasswordField
          label={t('reset.new_password')}
          name="newPassword"
          autoComplete="new-password"
          helperText={errors.newPassword}
          value={values.newPassword}
          onChange={handleChange}
          error={!!errors['newPassword']}
        />
      </FormLayout>
    );
  }

  if (step === 'wrongCode') {
    return (
      <FormLayout
        title={t('challenge.reset_password.invalid_code')}
        onSubmit={handleSubmit}
        errors={{ ...errors, form: t('reset.invalid_token') }}
        submitting={submitting}
        submitLabel={t('challenge.reset_password.confirm')}
      >
        <TextField
          label={t('challenge.reset_password.code')}
          name="code"
          helperText={errors.code}
          value={values.code}
          onChange={handleChange}
          error={!!errors['code']}
        />
        <ResendCodeLink duration={10} onClick={() => mutate()} />
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title={t('challenge.reset_password.title')}
      errors={errors}
      submitting={submitting}
      onSubmit={handleSubmit}
      submitLabel={t('challenge.reset_password.confirm')}
    >
      <p>{t('challenge.reset_password.text', { siteName })}</p>
      <TextField
        label={t('challenge.reset_password.code')}
        name="code"
        helperText={errors.code}
        value={values.code}
        onChange={handleChange}
        error={!!errors['code']}
      />
      <ResendCodeLink onClick={() => mutate()} />
      {isLoading && <LoadingOverlay />}
    </FormLayout>
  );
};

export default ResetPasswordChallenge;
